import { useQuery } from '@tanstack/react-query';
import { metricTypeTransformers } from 'Deer/hooks/dataHooks/metrics/utils/overviewMetricsUtils';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import {
  getAggregatedScore,
  getEngagementCount,
  getExpandedTabularData,
  getTabularData,
  getTimelineData,
} from 'services/analytics/engagement';
import { getFilters } from 'services/analytics/engagement';
import { getActiveSurveys, getArchivedSurveys } from 'services/engagement';

const getData = async ({ parentUuid, splitByKeys, dateRange, source, groupByKeys, cohort }) => {
  try {
    const res = await getExpandedTabularData({
      cohort,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
      dateRange,
    });
    return res;
  } catch (err) {
    console.error('Error fetching expanded engagement data : ', err);
  }
};

export const useExpandedEngagementData = ({ parentUuid, splitByKeys, dateRange, source, groupByKeys, cohort }) =>
  useQuery(
    [REACT_QUERY_KEYS.EngagementExpandedTable, parentUuid, cohort?.id, splitByKeys, dateRange, source, groupByKeys],
    () =>
      getData({
        parentUuid,
        splitByKeys,
        dateRange,
        source,
        groupByKeys,
        cohort,
      }),
    { enabled: !!parentUuid }
  );

const getEngagementCountData = async () => {
  try {
    const res = await getEngagementCount();
    return res;
  } catch (err) {
    console.error('Error fetching engagement count data : ', err);
  }
};

export const useEngagementCountData = () =>
  useQuery([REACT_QUERY_KEYS.EngagementCountData], () => getEngagementCountData());

const getActiveSurveysData = async ({ limit, offset, includeCount }) => {
  try {
    const res = await getActiveSurveys({ limit, offset, includeCount });
    return res;
  } catch (err) {
    console.error('Error fetching active survey data : ', err);
  }
};

export const useEngagementActiveSurveyData = ({ limit, offset, includeCount }) =>
  useQuery([REACT_QUERY_KEYS.EngagementActiveSurveys, limit, offset, includeCount], () =>
    getActiveSurveysData({ limit, offset, includeCount })
  );

const getSubmittedSurveysData = async ({ limit, offset, includeCount }) => {
  try {
    const res = await getArchivedSurveys({ limit, offset, includeCount });
    return res;
  } catch (err) {
    console.error('Error fetching active survey data : ', err);
  }
};

export const useEngagementSubmittedSurveyData = ({ limit, offset, includeCount }) =>
  useQuery([REACT_QUERY_KEYS.EngagementSubmittedSurveys, limit, offset, includeCount], () =>
    getSubmittedSurveysData({ limit, offset, includeCount })
  );

const getEngagementLineChartData = async ({ cohort, dateRange, groupByKeys, splitByKeys, source, parentUuid }) => {
  try {
    const res = await getTimelineData({
      cohort,
      dateRange,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
    });
    return res;
  } catch (err) {
    console.error('Error fetching active survey data : ', err);
  }
};

export const useEngagementLineChartData = ({ cohort, dateRange, groupByKeys, splitByKeys, source, parentUuid }) =>
  useQuery(
    [REACT_QUERY_KEYS.EngagementLineChartData, cohort?.id, dateRange, groupByKeys, splitByKeys, source, parentUuid],
    () =>
      getEngagementLineChartData({
        cohort,
        dateRange,
        groupByKeys,
        splitByKeys,
        source,
        parentUuid,
      }),
    {
      enabled: !!cohort,
    }
  );

const getEngagementAggregatedScoreData = async ({
  cohort,
  dateRange,
  groupByKeys,
  splitByKeys,
  source,
  parentUuid,
}) => {
  try {
    const res = await getAggregatedScore({
      cohort,
      dateRange,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
    });
    return res;
  } catch (err) {
    console.error('Error fetching active survey data : ', err);
  }
};

export const useEngagementAggregatedScoreData = ({ cohort, dateRange, groupByKeys, splitByKeys, source, parentUuid }) =>
  useQuery(
    [REACT_QUERY_KEYS.EngagementAggregatedChartData, cohort?.id, dateRange, groupByKeys, splitByKeys, source],
    () =>
      getEngagementAggregatedScoreData({
        cohort,
        dateRange,
        groupByKeys,
        splitByKeys,
        source,
        parentUuid,
      }),
    {
      enabled: !!cohort,
    }
  );

export const getEngagementSurveyReportsApi = ({
  cohortId,
  feedbackFormIds = [],
  limit = 1000,
  pivotMode = false,
  rowGroupCols = [],
  pivotCols,
  dateRange,
  filterModel = {},
}) => {
  const url = endpoints.performanceReview.insights.generateReport;
  if (!cohortId || !feedbackFormIds.length) {
    return Promise.resolve({ entity: { data: [] } });
  }
  const res = sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      reportType: 'ENGAGEMENT_RATINGS',
      cohortId,
      limit,
      baseFilter: {
        feedbackFormIds,
        dateRange,
      },
      pivotMode,
      rowGroupCols,
      pivotCols,
      filterModel,
    },
  });
  return res;
};

export const useGetEngagementSurveyReports = ({
  cohort,
  feedbackFormIds = [],
  rowGroupCols = [],
  pivotCols = [],
  pivotMode = true,
  selectedSplitById,
  dateRange,
}) =>
  useQuery(
    [
      REACT_QUERY_KEYS.EngagementSurveyReports,
      cohort?.id,
      feedbackFormIds.join(','),
      rowGroupCols.map((col) => col.colId).join(','),
      selectedSplitById,
    ],
    () =>
      getEngagementSurveyReportsApi({
        cohortId: cohort?.id,
        feedbackFormIds,
        rowGroupCols,
        pivotCols,
        pivotMode,
        dateRange,
      }),
    {
      enabled: !!cohort && feedbackFormIds.length > 0,
    }
  );

export const getEngagementSurveyReportsColumnsApi = () => {
  const url = endpoints.performanceReview.insights.getReportColumns;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { reportType: 'ENGAGEMENT_RATINGS' },
  });
};

export const getEngagementSurveyReportsLineChart = async ({
  cohortId,
  pivotMode = false,
  rowGroupCols = [],
  pivotCols,
  dateRange,
  filterModel = {},
  feedbackFormIds,
  limit,
}) => {
  const url = endpoints.performanceReview.insights.getReportLineChart;
  const res = await sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    queryParams: { graphType: 'TREND_LINE' },
    body: {
      reportType: 'ENGAGEMENT_RATINGS',
      cohortId,
      limit,
      baseFilter: {
        feedbackFormIds,
        dateRange,
      },
      pivotMode,
      rowGroupCols,
      pivotCols,
      filterModel,
    },
  });

  return res;
};

export const useGetEngagementSurveyReportsLineChart = ({
  cohort,
  feedbackFormIds = [],
  rowGroupCols = [],
  pivotCols = [],
  pivotMode = true,
  selectedSplitById,
  dateRange,
}) =>
  useQuery(
    [
      REACT_QUERY_KEYS.EngagementSurveyReportsLineChart,
      cohort?.id,
      feedbackFormIds.join(','),
      rowGroupCols.map((col) => col.colId).join(','),
      selectedSplitById,
      dateRange,
    ],
    () =>
      getEngagementSurveyReportsLineChart({
        cohortId: cohort?.id,
        feedbackFormIds,
        rowGroupCols,
        pivotCols,
        pivotMode,
        dateRange,
      }),
    {
      enabled: !!cohort && feedbackFormIds.length > 0,
    }
  );

export const useGetEngagementSurveyReportsColumns = () =>
  useQuery([REACT_QUERY_KEYS.EngagementSurveyReportsColumns], () => getEngagementSurveyReportsColumnsApi());

const getEngagementPerformanceGridData = async ({
  cohort,
  dateRange,
  groupByKeys,
  splitByKeys,
  source,
  parentUuid,
}) => {
  try {
    const res = await getTabularData({
      cohort,
      dateRange,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
    });
    return res;
  } catch (err) {
    console.error('Error fetching active survey data : ', err);
  }
};

export const useEngagementPerformanceGridData = ({ cohort, dateRange, groupByKeys, splitByKeys, source, parentUuid }) =>
  useQuery(
    [
      REACT_QUERY_KEYS.EngagementPerformanceGridData,
      cohort?.id,
      dateRange,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
    ],
    () =>
      getEngagementPerformanceGridData({
        cohort,
        dateRange,
        groupByKeys,
        splitByKeys,
        source,
        parentUuid,
      }),
    {
      enabled: !!cohort,
    }
  );

export const useEngagementPerformanceGridClickThroughData = ({
  cohort,
  dateRange,
  groupByKeys,
  splitByKeys,
  source,
  parentUuid,
}) =>
  useQuery(
    [
      REACT_QUERY_KEYS.EngagementPerformanceGridClickedData,
      cohort?.id,
      dateRange,
      groupByKeys,
      splitByKeys,
      source,
      parentUuid,
    ],
    () =>
      getEngagementPerformanceGridData({
        cohort,
        dateRange,
        groupByKeys,
        splitByKeys,
        source,
        parentUuid,
      }),
    {
      enabled: !!cohort,
    }
  );

const getFilterSources = async () => {
  try {
    const res = await getFilters();
    return res;
  } catch (err) {
    console.error('Error fetching engagement filter sources : ', err);
  }
};

export const useEngagementFilterSources = () =>
  useQuery([REACT_QUERY_KEYS.EngagementFilterSources], () => getFilterSources());
